import Marionette from "backbone.marionette";
import Radio from "backbone.radio";
import ChosenBehavior from "../../../behaviors/chosen";
import FilteredModel from "../../../models/chart-reviews/bulk-assign-filtered";
import SelectedModel from "../../../models/chart-reviews/bulk-assign-selected";
import templates from "../../../utils/templates";
import { errorParser, numberFormatter } from "../../../utils";
import confirmationAlert from "../../../utils/alerts/confirmation";

import "../../../../../sass/chart-reviews-bulk-assignments.scss";

const channel = Radio.channel("global");

const DEFAULTS = {
  tagName: "form",
  template: templates.find("chart_reviews", "chart_bulk_assign_form"),
};

class View extends Marionette.ItemView {
  constructor(options = {}) {
    _.defaults(options, DEFAULTS);
    super(options);
  }

  behaviors() {
    return [{ behaviorClass: ChosenBehavior }];
  }

  ui() {
    return {
      limitedTo: "#limitedTo",
      reviewers: "#reviewers",
      keepExistingReviewers: "#keepExistingReviewers",
      resetBtn: "button[type=reset]",
      submitBtn: "button[type=submit]",
      savingBtn: "button[type=button]",
    };
  }

  events() {
    return {
      "change @ui.limitedTo": "_onReviewersChange",
      "change @ui.reviewers": "_onReviewersChange",
      "click @ui.keepExistingReviewers": "_onClickKeepExistingReviewers",
      reset: "_onReset",
      submit: "_onSubmit",
    };
  }

  initialize() {
    this.payload = this.options.payload;
    this.emptyFormModel = this.model.toJSON();

    // make sure to render the view when the model changes
    this.listenTo(this.model, "change", this._onModelChange);
  }

  onAttach() {
    this._notifyBehaviors();
  }

  _onModelChange() {
    this.render();
    this._notifyBehaviors();
  }

  _notifyBehaviors() {
    this.triggerMethod("ContentRendered");
  }

  // extracts selected reviewers
  _onReviewersChange() {
    const $selected = this.ui.reviewers.find(":selected");
    const selectedReviewers = $selected
      .get()
      .map((option) => Number(option.value));

    this._updateSelectedReviewers(selectedReviewers);
  }

  // marks selected reviewers on model
  _updateSelectedReviewers(reviewers) {
    // update totals
    let limitedTo = Number(this.ui.limitedTo.val().trim());
    if (limitedTo < 1) {
      // reset count to original limit
      limitedTo = this.emptyFormModel.limitedTo;
    }

    // distribute charts equally amongst reviewers
    let evenSplit = Math.floor(limitedTo / reviewers.length);
    evenSplit = Infinity === evenSplit ? limitedTo : evenSplit;

    const chartAssignments = reviewers.map((id) => ({ id, count: evenSplit }));
    // spread remainder
    let remainder = Number(limitedTo % reviewers.length);
    remainder = isNaN(remainder) ? limitedTo : remainder;

    while (remainder--) {
      chartAssignments[remainder].count++;
    }

    // grab copy of collection
    const coll = this.model.getReviewersList();

    // uncheck all reviewers
    coll.each((model) => model.setChecked(false));

    // check selected reviewers and set charts assigned
    reviewers.forEach((id) => {
      const assignment = chartAssignments.filter((i) => id === i.id);
      if (assignment && assignment.length) {
        coll.get(id).set({
          checked: true,
          bulkAssignedChartCount: assignment[0].count,
        });
      }
    });

    // update model
    this.model.set({
      reviewersList: coll.toJSON(),
      reviewersCount: reviewers.length,
      limitedTo,
    });
  }

  _onClickKeepExistingReviewers() {
    this.model.set({
      keepExistingReviewers: this.ui.keepExistingReviewers.get(0).checked,
    });
  }

  // overrides the default reset behavior. it resets the model instead of the
  // markup.
  _onReset(e) {
    e.preventDefault();

    // reset model with setup data
    this.model.set(this.emptyFormModel);
  }

  _onSubmit(e) {
    e.preventDefault();

    // extract submitted form data
    const data = Backbone.Syphon.serialize(this);

    // process assignments
    if (data.reviewers && Array.isArray(data.reviewers)) {
      const assignments = data.reviewers.map((reviewerId, index) => {
        let count = Number(data.assignments[index]);
        if (!_.isNumber(count) || _.isNaN(count)) {
          count = 0;
        }

        return {
          id: Number(reviewerId),
          count,
        };
      });
      data.assignments = assignments;
    }

    const payload = Object.assign(
      {},
      { filters: (this.payload && this.payload.filters) || {} },
      { data }
    );

    // add IDs when they exist (selected rows)
    if (this.payload && this.payload.ids) {
      payload["ids"] = this.payload.ids;
    }

    // create API model
    let model = null;
    if (payload.ids) {
      model = new SelectedModel(payload);
    } else {
      model = new FilteredModel(payload);
    }

    // validate it
    if (model.isValid()) {
      this._disableFormButtons();

      // display confirmation
      const title = this._getConfirmationMessage(model);
      confirmationAlert({ title }).then((result) => {
        if (result.value) {
          // submit on acceptance
          this._saveModel(model);
        } else {
          this._enableFormButtons();
        }
      });
    } else {
      this._enableFormButtons();

      // show warning message
      channel.trigger("status:show", {
        type: "warning",
        message: model.validationError,
      });
    }
  }

  _getConfirmationMessage(model) {
    const plural = this.model.getChartCount() > 1 ? "s" : "";
    const message = `You have chosen to modify ${numberFormatter(
      model.getData().getLimitedTo()
    )} chart${plural}.`;

    return message;
  }

  _saveModel(model) {
    // attempt to update chart
    model.save(model.attrs, {
      success: this._onSync.bind(this),
      error: this._onSyncFailure.bind(this),
    });
  }

  _onSync() {
    // show success message
    channel.trigger("status:show", {
      type: "success",
      message: "Your charts were updated successfully.",
    });

    // notify parent layout of this change
    this.triggerMethod("dismiss:modal");
  }

  _onSyncFailure(model, response) {
    // show error
    channel.trigger("status:show", {
      autoDismiss: false,
      type: "danger",
      message: errorParser(response),
    });
  }

  _disableFormButtons() {
    this.ui.resetBtn.css("display", "none");
    this.ui.submitBtn.css("display", "none");
    this.ui.savingBtn.removeClass("hidden");
  }

  _enableFormButtons() {
    this.ui.resetBtn.css("display", "inline-block");
    this.ui.submitBtn.css("display", "inline-block");
    this.ui.savingBtn.addClass("hidden");
  }
}

export { View as default };
